/**
 * @file 과업 요청서 — 실행팀·영상팀·디자인팀에 보내는 업무 요청 티켓.
 *
 * 기존엔 한 건의 요청이 메일 여러 통으로 흩어져 "최신본이 뭐냐"를 사람이 기억해야 했고,
 * 요청 때마다 "포맷 다운로드하세요" 를 말해야 했다. 이 페이지는 둘 다 없앤다.
 *   · 팀·유형을 고르면 양식이 작성 화면에 바로 뜨고, 작성본을 올려야 요청이 나간다
 *   · 이후 모든 왕복(수정본·결과물·피드백)이 티켓 하나에 버전으로 쌓이고 최신본이 기본 표시
 *
 * 탭 구성: 과업 요청서 / 업무 메뉴얼 / 양식 관리(권한자만)
 * 딥링크: ?tab=manual, ?openWorkRequest={id} (알림 클릭 진입 — NotificationService 의 linkUrl 컨벤션)
 */
import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Box, Tab, Tabs, Typography } from "@mui/material";

import WorkRequestBoard from "./components/WorkRequestBoard";
import WorkRequestComposeModal from "./components/WorkRequestComposeModal";
import WorkRequestDetailModal from "./components/WorkRequestDetailModal";
import WorkManualTab from "./components/WorkManualTab";
import WorkRequestTypeAdminTab from "./components/WorkRequestTypeAdminTab";
import { workRequestApi } from "./api/workRequestApi";
import { useMyPermissions } from "../../util/useMyPermissions";
import { ds } from "../../styles/designSystem";

const C = ds.colors;

// 별도 권한 코드 없음 — 세 탭 모두 전 직원에게 열려 있고, 등록/수정 가능 범위는
// '내 소속 팀 + 내가 만든 것' 기준으로 각 탭이 항목별로 판단한다(서버도 같은 규칙을 강제).
const TABS = [
    { key: "request", label: "과업 요청서" },
    { key: "manual",  label: "업무 메뉴얼" },
    { key: "format",  label: "요청 양식" },
];

export default function WorkRequestPage() {
    const history = useHistory();
    const location = useLocation();
    // 전체 관리자는 소속과 무관하게 모든 팀의 양식·메뉴얼을 등록·수정할 수 있다(백엔드 가드도 동일하게 우회).
    const { isAdmin } = useMyPermissions();

    const [departments, setDepartments] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [me, setMe] = useState(null);

    const [tab, setTab] = useState("request");
    const [composeOpen, setComposeOpen] = useState(false);
    const [detailId, setDetailId] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);

    // 참조 데이터는 한 번만 — 팀 목록·직원 목록은 탭을 오가도 그대로 쓴다.
    useEffect(() => {
        workRequestApi.getDepartments().then(d => setDepartments(Array.isArray(d) ? d : [])).catch(() => {});
        workRequestApi.getEmployees().then(e => setEmployees(Array.isArray(e) ? e : [])).catch(() => {});
        workRequestApi.getMe().then(setMe).catch(() => {});
    }, []);

    /**
     * URL → 화면 상태. <b>{@code location.search} 가 바뀔 때마다</b> 다시 해석한다.
     *
     * <p>예전엔 마운트 1회만 읽어서, 이미 이 페이지에 있는 상태로 사이드바의 '업무 메뉴얼' 을 눌러도
     * 탭이 안 바뀌었다(주소만 바뀌고 컴포넌트는 리마운트되지 않으므로). 게다가 해석 후 쿼리를 통째로
     * 지워버려서 '요청 목록'(쿼리 없는 같은 주소)을 누르면 아예 아무 일도 일어나지 않았다.
     *
     * <p>이제 탭은 URL 이 단일 출처다 — 화면에서 탭을 눌러도 주소를 갱신한다. 상세 모달을 여는
     * {@code ?openWorkRequest} 만 1회성이라 해석 후 <b>그 파라미터만</b> 제거한다(tab 은 남긴다).
     */
    useEffect(() => {
        const q = new URLSearchParams(location.search);
        const openId = q.get("openWorkRequest");
        const wantTab = q.get("tab");

        setTab(wantTab && TABS.some(t => t.key === wantTab) ? wantTab : "request");

        if (openId) {
            setDetailId(Number(openId));
            q.delete("openWorkRequest");
            const rest = q.toString();
            history.replace(`/work-request${rest ? `?${rest}` : ""}`);
        }
    }, [location.search, history]);

    /**
     * 탭 클릭 — 주소를 갱신해 URL 을 단일 출처로 유지한다(위 effect 가 받아 탭을 바꾼다).
     * 사이드바 링크와 같은 모양(?tab=)으로 맞춰야 메뉴 활성 표시가 어긋나지 않는다.
     */
    const selectTab = (key) => history.replace(`/work-request?tab=${key}`);

    const bumpReload = useCallback(() => setReloadKey(k => k + 1), []);

    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
            <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#0f172a", mb: 0.3 }}>
                과업 요청서
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: C.textMuted, mb: 2 }}>
                팀에 업무를 요청하고, 요청본·결과본을 한 곳에서 버전으로 주고받습니다.
            </Typography>

            <Tabs
                value={tab}
                onChange={(_, v) => selectTab(v)}
                sx={{
                    minHeight: 38, borderBottom: `1px solid ${C.border}`, mb: 2.5,
                    "& .MuiTab-root": { minHeight: 38, fontSize: 13, fontWeight: 600, textTransform: "none" },
                }}
            >
                {TABS.map(t => <Tab key={t.key} value={t.key} label={t.label} />)}
            </Tabs>

            {tab === "request" && (
                <WorkRequestBoard
                    reloadKey={reloadKey}
                    onOpen={setDetailId}
                    onCompose={() => setComposeOpen(true)}
                />
            )}

            {tab === "manual" && <WorkManualTab departments={departments} me={me} isAdmin={isAdmin} />}

            {tab === "format" && <WorkRequestTypeAdminTab departments={departments} me={me} isAdmin={isAdmin} />}

            <WorkRequestComposeModal
                open={composeOpen}
                onClose={() => setComposeOpen(false)}
                departments={departments}
                employees={employees}
                onCreated={(created) => { bumpReload(); if (created?.id) setDetailId(created.id); }}
            />

            <WorkRequestDetailModal
                open={Boolean(detailId)}
                requestId={detailId}
                myId={me?.id}
                onClose={() => setDetailId(null)}
                onChanged={bumpReload}
            />
        </Box>
    );
}
